'use client';

import {
    Timeline,
    TimelineBody,
    TimelineConnector,
    TimelineHeader,
    TimelineIcon,
    TimelineItem,
    Typography,
} from '@material-tailwind/react';
import { motion } from 'framer-motion';

export default function BusinessConsultTimeline() {
    // Animation variants for timeline items
    const itemVariants = {
        hidden: { opacity: 0, x: -50 },
        visible: (i) => ({
            opacity: 1,
            x: 0,
            transition: {
                delay: i * 0.2,
                duration: 0.6,
                ease: 'easeOut',
            },
        }),
    };

    // Icon animation variants
    const iconVariants = {
        hidden: { scale: 0, rotate: -180 },
        visible: {
            scale: 1,
            rotate: 0,
            transition: {
                type: 'spring',
                stiffness: 200,
                damping: 15,
            },
        },
        hover: {
            scale: 1.1,
            rotate: 5,
            transition: {
                duration: 0.3,
            },
        },
    };

    // List item animation variants
    const listItemVariants = {
        hidden: { opacity: 0, y: 10 },
        visible: (i) => ({
            opacity: 1,
            y: 0,
            transition: {
                delay: 0.3 + i * 0.1,
                duration: 0.4,
            },
        }),
    };

    return (
        <div className="relative py-12 px-4">
            {/* Background gradient decoration */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-transparent to-purple-50 opacity-50 pointer-events-none rounded-3xl" />

            <Timeline className="md:w-[50rem] text-[#3f3e3e] relative">
                {/* Timeline Item 1 */}
                <motion.div
                    custom={0}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: '-100px' }}
                    variants={itemVariants}
                >
                    <TimelineItem>
                        <TimelineConnector className="!bg-gradient-to-b from-[#528ff6] to-[#7ba9f8]" />
                        <TimelineHeader>
                            <motion.div
                                variants={iconVariants}
                                initial="hidden"
                                whileInView="visible"
                                whileHover="hover"
                                viewport={{ once: true }}
                            >
                                <TimelineIcon className="p-3 rounded-full bg-gradient-to-br from-[#528ff6] to-[#3d7ae0] shadow-lg shadow-blue-300/50 ring-4 ring-blue-100">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        height="24"
                                        width="24"
                                        fill="#fff"
                                        viewBox="0 0 576 512"
                                    >
                                        <path d="M312 24V34.5c6.4 1.2 12.6 2.7 18.2 4.2c12.8 3.4 20.4 16.6 17 29.4s-16.6 20.4-29.4 17c-10.9-2.9-21.1-4.9-30.2-5c-7.3-.1-14.7 1.7-19.4 4.4c-2.1 1.3-3.1 2.4-3.5 3c-.3 .5-.7 1.2-.7 2.8c0 .3 0 .5 0 .6c.2 .2 .9 1.2 3.3 2.6c5.8 3.5 14.4 6.2 27.4 10.1l.9 .3c11.1 3.3 25.9 7.8 37.9 15.3c13.7 8.6 26.1 22.9 26.4 44.9c.3 22.5-11.4 38.9-26.7 48.5c-6.7 4.1-13.9 7-21.3 8.8V232c0 13.3-10.7 24-24 24s-24-10.7-24-24V220.6c-9.5-2.3-18.2-5.3-25.6-7.8c-2.1-.7-4.1-1.4-6-2c-12.6-4.2-19.4-17.8-15.2-30.4s17.8-19.4 30.4-15.2c2.6 .9 5 1.7 7.3 2.5c13.6 4.6 23.4 7.9 33.9 8.3c8 .3 15.1-1.6 19.2-4.1c1.9-1.2 2.8-2.2 3.2-2.9c.4-.6 .9-1.8 .8-4.1l0-.2c0-1 0-2.1-4-4.6c-5.7-3.6-14.3-6.4-27.1-10.3l-1.9-.6c-10.8-3.2-25-7.5-36.4-14.4c-13.5-8.1-26.5-22-26.6-44.1c-.1-22.9 12.9-38.6 27.7-47.4c6.4-3.8 13.3-6.4 20.2-8.2V24c0-13.3 10.7-24 24-24s24 10.7 24 24zM568.2 336.3c13.1 17.8 9.3 42.8-8.5 55.9L433.1 485.5c-23.4 17.2-51.6 26.5-80.7 26.5H192 32c-17.7 0-32-14.3-32-32V416c0-17.7 14.3-32 32-32H68.8l44.9-36c22.7-18.2 50.9-28 80-28H272h16 64c17.7 0 32 14.3 32 32s-14.3 32-32 32H288 272c-8.8 0-16 7.2-16 16s7.2 16 16 16H392.6l119.7-88.2c17.8-13.1 42.8-9.3 55.9 8.5zM193.6 384l0 0-.9 0c.3 0 .6 0 .9 0z" />
                                    </svg>
                                </TimelineIcon>
                            </motion.div>
                            <Typography
                                variant="h5"
                                className="text-gray-800 font-bold bg-gradient-to-r from-[#528ff6] to-[#3d7ae0] bg-clip-text text-transparent"
                            >
                                Tax Administration - Simplified
                            </Typography>
                        </TimelineHeader>
                        <TimelineBody className="pb-12">
                            <motion.div
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true }}
                                className="bg-white/70 backdrop-blur-sm p-6 rounded-2xl shadow-md hover:shadow-xl transition-shadow duration-300 border border-blue-100"
                            >
                                <ul className="flex flex-col gap-4">
                                    {[
                                        'Stuck with confusing tax documents? Let our professional chartered accountants handle it. We ensure discretion, precision and adhere to the rules and regulations. Let us bring in the deductions.',
                                        'With our proactive approach, which keeps you informed and compliant throughout the year, you can eliminate the stress associated with taxes.',
                                    ].map((text, i) => (
                                        <motion.li
                                            key={i}
                                            custom={i}
                                            variants={listItemVariants}
                                            className="flex items-start gap-3 text-gray-600 leading-relaxed"
                                        >
                                            <span className="flex-shrink-0 w-2 h-2 mt-2 rounded-full bg-gradient-to-r from-[#528ff6] to-[#7ba9f8]" />
                                            <span>{text}</span>
                                        </motion.li>
                                    ))}
                                </ul>
                            </motion.div>
                        </TimelineBody>
                    </TimelineItem>
                </motion.div>

                {/* Timeline Item 2 */}
                <motion.div
                    custom={1}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: '-100px' }}
                    variants={itemVariants}
                >
                    <TimelineItem>
                        <TimelineConnector className="!bg-gradient-to-b from-[#528ff6] to-[#7ba9f8]" />
                        <TimelineHeader>
                            <motion.div
                                variants={iconVariants}
                                initial="hidden"
                                whileInView="visible"
                                whileHover="hover"
                                viewport={{ once: true }}
                            >
                                <TimelineIcon className="p-3 rounded-full bg-gradient-to-br from-[#528ff6] to-[#3d7ae0] shadow-lg shadow-blue-300/50 ring-4 ring-blue-100">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        height="24"
                                        width="24"
                                        fill="#fff"
                                        viewBox="0 0 384 512"
                                    >
                                        <path d="M64 48c-8.8 0-16 7.2-16 16V448c0 8.8 7.2 16 16 16h80V400c0-26.5 21.5-48 48-48s48 21.5 48 48v64h80c8.8 0 16-7.2 16-16V64c0-8.8-7.2-16-16-16H64zM0 64C0 28.7 28.7 0 64 0H320c35.3 0 64 28.7 64 64V448c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V64zm88 40c0-8.8 7.2-16 16-16h48c8.8 0 16 7.2 16 16v48c0 8.8-7.2 16-16 16H104c-8.8 0-16-7.2-16-16V104zM232 88h48c8.8 0 16 7.2 16 16v48c0 8.8-7.2 16-16 16H232c-8.8 0-16-7.2-16-16V104c0-8.8 7.2-16 16-16zM88 232c0-8.8 7.2-16 16-16h48c8.8 0 16 7.2 16 16v48c0 8.8-7.2 16-16 16H104c-8.8 0-16-7.2-16-16V232zm144-16h48c8.8 0 16 7.2 16 16v48c0 8.8-7.2 16-16 16H232c-8.8 0-16-7.2-16-16V232c0-8.8 7.2-16 16-16z" />
                                    </svg>
                                </TimelineIcon>
                            </motion.div>
                            <Typography
                                variant="h5"
                                className="text-gray-800 font-bold bg-gradient-to-r from-[#528ff6] to-[#3d7ae0] bg-clip-text text-transparent"
                            >
                                Your Organization&apos;s Structure - Revamped
                            </Typography>
                        </TimelineHeader>
                        <TimelineBody className="pb-12">
                            <motion.div
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true }}
                                className="bg-white/70 backdrop-blur-sm p-6 rounded-2xl shadow-md hover:shadow-xl transition-shadow duration-300 border border-blue-100"
                            >
                                <ul className="flex flex-col gap-4">
                                    {[
                                        "Don't let operational bottlenecks stop you. Let our team be your solution partner. We identify and resolve your operational flow with expertise.",
                                        'Our team will revamp your activity structure to improve your performance and efficiency to be in par with your business goals and needs.',
                                    ].map((text, i) => (
                                        <motion.li
                                            key={i}
                                            custom={i}
                                            variants={listItemVariants}
                                            className="flex items-start gap-3 text-gray-600 leading-relaxed"
                                        >
                                            <span className="flex-shrink-0 w-2 h-2 mt-2 rounded-full bg-gradient-to-r from-[#528ff6] to-[#7ba9f8]" />
                                            <span>{text}</span>
                                        </motion.li>
                                    ))}
                                </ul>
                            </motion.div>
                        </TimelineBody>
                    </TimelineItem>
                </motion.div>

                {/* Timeline Item 3 */}
                <motion.div
                    custom={2}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: '-100px' }}
                    variants={itemVariants}
                >
                    <TimelineItem>
                        <TimelineHeader>
                            <motion.div
                                variants={iconVariants}
                                initial="hidden"
                                whileInView="visible"
                                whileHover="hover"
                                viewport={{ once: true }}
                            >
                                <TimelineIcon className="p-3 rounded-full bg-gradient-to-br from-[#528ff6] to-[#3d7ae0] shadow-lg shadow-blue-300/50 ring-4 ring-blue-100">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        height="24"
                                        width="24"
                                        viewBox="0 0 384 512"
                                        fill="#fff"
                                    >
                                        <path d="M64 48c-8.8 0-16 7.2-16 16V448c0 8.8 7.2 16 16 16h80V400c0-26.5 21.5-48 48-48s48 21.5 48 48v64h80c8.8 0 16-7.2 16-16V64c0-8.8-7.2-16-16-16H64zM0 64C0 28.7 28.7 0 64 0H320c35.3 0 64 28.7 64 64V448c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V64zm88 40c0-8.8 7.2-16 16-16h48c8.8 0 16 7.2 16 16v48c0 8.8-7.2 16-16 16H104c-8.8 0-16-7.2-16-16V104zM232 88h48c8.8 0 16 7.2 16 16v48c0 8.8-7.2 16-16 16H232c-8.8 0-16-7.2-16-16V104c0-8.8 7.2-16 16-16zM88 232c0-8.8 7.2-16 16-16h48c8.8 0 16 7.2 16 16v48c0 8.8-7.2 16-16 16H104c-8.8 0-16-7.2-16-16V232zm144-16h48c8.8 0 16 7.2 16 16v48c0 8.8-7.2 16-16 16H232c-8.8 0-16-7.2-16-16V232c0-8.8 7.2-16 16-16z" />
                                    </svg>
                                </TimelineIcon>
                            </motion.div>
                            <Typography
                                variant="h5"
                                className="text-gray-800 font-bold bg-gradient-to-r from-[#528ff6] to-[#3d7ae0] bg-clip-text text-transparent"
                            >
                                The power of ERP Automation in your hands
                            </Typography>
                        </TimelineHeader>
                        <TimelineBody>
                            <motion.div
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true }}
                                className="bg-white/70 backdrop-blur-sm p-6 rounded-2xl shadow-md hover:shadow-xl transition-shadow duration-300 border border-blue-100"
                            >
                                <ul className="flex flex-col gap-5">
                                    <motion.li
                                        custom={0}
                                        variants={listItemVariants}
                                        className="flex items-start gap-3 text-gray-600 leading-relaxed"
                                    >
                                        <span className="flex-shrink-0 w-2 h-2 mt-2 rounded-full bg-gradient-to-r from-[#528ff6] to-[#7ba9f8]" />
                                        <span>
                                            No more workloads and manual errors.
                                            Let our ERP software solutions
                                            optimize your financial management
                                            and workflows, reducing mistakes and
                                            boosting productivity.
                                        </span>
                                    </motion.li>
                                    <motion.li
                                        custom={1}
                                        variants={listItemVariants}
                                        className="flex items-start gap-3 text-gray-600 leading-relaxed"
                                    >
                                        <span className="flex-shrink-0 w-2 h-2 mt-2 rounded-full bg-gradient-to-r from-[#528ff6] to-[#7ba9f8]" />
                                        <span>
                                            The leading{' '}
                                            <span className="font-semibold text-[#528ff6]">
                                                ERP Company in Bangalore
                                            </span>
                                            , Wahni IT Solutions, offers new,
                                            cutting-edge and innovative ERP
                                            software solutions that produce
                                            measurable outcomes. From efficient
                                            tax management to automated
                                            financial processes, we provide
                                            complete ERP software solutions that
                                            meet your unique business demands.
                                        </span>
                                    </motion.li>
                                    <motion.div
                                        custom={2}
                                        variants={listItemVariants}
                                        className="mt-2 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border-l-4 border-[#528ff6]"
                                    >
                                        <p className="text-gray-700 leading-relaxed">
                                            Are you trying to find a reliable
                                            ERP supplier?
                                            <br />
                                            Or is it{' '}
                                            <span className="font-semibold text-[#528ff6]">
                                                ERPnext in Bangalore
                                            </span>{' '}
                                            that you are looking for?
                                            <br />
                                            We are here to help you with your{' '}
                                            <span className="font-semibold text-[#528ff6]">
                                                ERP Software in Bangalore
                                            </span>{' '}
                                            needs. Working with us will allow
                                            you to experience future business
                                            effectiveness.
                                        </p>
                                    </motion.div>
                                </ul>
                            </motion.div>
                        </TimelineBody>
                    </TimelineItem>
                </motion.div>
            </Timeline>
        </div>
    );
}
